import * as React from "react";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker, useNavigate } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultServiceAccountInput, inputToServiceAccount } from "../utils";
import { ServiceAccountInput } from "../../../schema/types/serviceAccount/input";
import { clearItem } from "../../../services/utils";
import { cloneDeep } from "lodash";
import { getValidator } from "./validator";
import { ServiceAccountCreateContent } from "./content";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { serviceAccountApi } from "../../../store/api/serviceAccountApi";

interface Props {}
export const ServiceAccountCreateForm: React.FC<Props> = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultServiceAccountInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/serviceaccount/-create`) &&
      !nextLocation.pathname.includes(`/serviceaccount/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: ServiceAccountInput) {
    try {
      let body = inputToServiceAccount(input);
      const method = "post";
      const url = homeLink("serviceaccount");

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("serviceaccount", body.name);
      dispatch(serviceAccountApi.util.invalidateTags([{ type: "ServiceAccount", id: itemLink }]));

      if (input.group.item) {
        try {
          const group = cloneDeep(input.group.item);
          clearItem(group);
          if (!group.memberLinks) {
            group.memberLinks = [];
          }
          group.memberLinks.push(itemLink);
          await request({ method: "patch", url: input.group.link, body: group });
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          notification.warning({
            message: "Failed to add to the group.",
            description: (
              <div>
                <div>Service Account is created but could not be added to the selected group.</div>
                <div>{errorMessage}</div>
              </div>
            ),
          });
        }
      }

      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Service Account is created",
      });
      dispatch(setRedirect(`/console${itemLink}`));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <ServiceAccountCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal
        inputToItemFn={inputToServiceAccount}
        kind="serviceaccount"
        namePath="name"
        title="Service Account"
      />
      <CpDebugModal
        inputToItemFn={inputToServiceAccount}
        kind="serviceaccount"
        namePath="name"
        title="Service Account"
      />
    </CpFormContext.Provider>
  );
};
